import debounce from 'just-debounce'
import uniq from 'just-unique'
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { FaqSettingMinimal, FilterSearchLayout, SearchParams } from '@karakuri-web/faq-type'

import { FilterSearch } from '../components'
import { useFaqData } from '../contexts/FaqDataContext'
import { useFaqRouting } from '../contexts/FaqRoutingContext'

import { BreadcrumbWidget, BreadcrumbWidgetProps } from './BreadcrumbWidget'

function usePlaceholder(defaultPlaceholder: string, q: string[]) {
  const [placeholder, setPlaceholder] = useState('')
  useEffect(() => {
    if (defaultPlaceholder.startsWith('[hide]')) {
      const p = defaultPlaceholder.slice(6)
      setPlaceholder(q.length > 0 ? '' : p)
    } else {
      setPlaceholder(defaultPlaceholder)
    }
  }, [defaultPlaceholder, q])
  return placeholder
}

/**
 * FilterSearchWidget用の設定インターフェース
 */
export interface FilterSearchWidgetSettings extends FaqSettingMinimal {
  waitMs?: {
    complete?: number | null
  } | null
  useAutoSearch?: boolean | null
  useKnowledgeSearch?: boolean | null
  words?: {
    search?: {
      placeholder?: string | null
      message?: string | null
      keyword?: string | null
      showKeyword?: string | null
      hideKeyword?: string | null
    } | null
  } | null
}

interface PageProps<D, C, S> {
  data: D
  pageContext?: C
  path: string
  location?: { search: string }
  state?: S
}

type Props<T extends FilterSearchWidgetSettings = FilterSearchWidgetSettings> = Pick<
  PageProps<{ settingYaml: T }, Record<string, unknown>, SearchParams>,
  'data' | 'path'
> & {
  layout: FilterSearchLayout
  q: string[]
  breadcrumbWidgetProps?: BreadcrumbWidgetProps
  onSearch?: (q: string[], page?: number) => void
}

/**
 * 絞り込み検索ウィジェット
 * キーワード入力とサジェスト機能を提供する
 */
export function FilterSearchWidget<
  T extends FilterSearchWidgetSettings = FilterSearchWidgetSettings,
>({ layout, data, q, onSearch, breadcrumbWidgetProps, path }: Props<T>) {
  const { navigate } = useFaqRouting()
  const faqData = useFaqData()

  const botId = data.settingYaml.botId || ''
  const tenantId = data.settingYaml.tenantId
  const waitMs = data.settingYaml.waitMs?.complete || 500
  const useAutoSearch = data.settingYaml.useAutoSearch || false
  const useKnowledgeSearch = data.settingYaml.useKnowledgeSearch || false

  const [options, setOptions] = useState<string[]>([])
  const [keywords, setKeywords] = useState<string[]>([])
  const placeholder = usePlaceholder(data.settingYaml?.words?.search?.placeholder || '', q)
  const input = useRef('')

  const memoizedComplete = useMemo(
    () =>
      debounce(
        async (keyword: string) => {
          if (!useKnowledgeSearch) {
            return
          }
          // TODO tagsの量が多いとcloudfrontのリクエスト最大長を超える場合がある。現在、非使用。
          const result = await faqData.fetchComplete({ botId, tenantId, keyword, q, tags: [] })
          setOptions(result.complete || [])
        },
        waitMs,
        false,
        true
      ),
    [botId, tenantId, faqData, q, waitMs, useKnowledgeSearch]
  )
  const complete = useCallback((keyword: string) => memoizedComplete(keyword), [memoizedComplete])

  useEffect(() => {
    complete('')
    if (!useKnowledgeSearch) {
      return
    }
    // TODO tagsの量が多いとcloudfrontのリクエスト最大長を超える場合がある。現在、非使用。
    faqData.fetchSuggest({ botId, tenantId, q, tags: [] }).then(result => {
      setKeywords(result.suggest || [])
    })
  }, [botId, tenantId, faqData, complete, q, useKnowledgeSearch])

  const handleSearch = useCallback(
    (event?: FormEvent) => {
      if (event) {
        event.preventDefault()
      }
      if (input.current) {
        const newValue = uniq([...q, ...input.current.split(/\s+/).filter(x => x)])
        handleChange(newValue)
        input.current = ''
        return
      }
      if (onSearch) {
        onSearch(q)
        return
      }
      // カテゴリIDを保持するため、現在のパス名を使用
      const to = `${path === '/' ? '/search/' : path}?q=${q.join(' ')}`
      navigate(to, { state: { q, page: 1 } })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [q]
  )

  useEffect(() => {
    if (useAutoSearch && path === '/search/' && q.length > 0) {
      handleSearch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q])

  const handleInput = (value: string) => {
    if (value) {
      complete(value)
    }
    input.current = value
  }
  const handleChange = (newValue: string[]) => {
    if (onSearch) {
      onSearch(newValue, 1)
      return
    }
    // カテゴリIDを保持するため、現在のパス名を使用
    const to = `${path === '/' ? '/search/' : path}?q=${newValue.join(' ')}`
    navigate(to, { state: { q: newValue, page: 1 } })
  }
  const handleSelect = (value: string) => {
    handleChange(uniq([...q, value]))
  }
  return (
    // 絞り込み検索のsuggestがtenant毎に切り替わるようにkeyを設定
    <form
      onSubmit={handleSearch}
      key={tenantId}
      className="faq-filter-navigation-widget"
      data-testid="faq-filter-navigation-form"
    >
      <div className="faq-panel faq-filter-search">
        <h3 className="faq-panel-title">{layout.title || 'キーワード検索'}</h3>
        <FilterSearch
          title={data.settingYaml?.words?.search?.message || ''}
          onSearch={handleSearch}
          keywordProps={{
            keywords,
            title: data.settingYaml?.words?.search?.keyword || '',
            showKeywordButtonLabel: data.settingYaml?.words?.search?.showKeyword || '',
            hideKeywordButtonLabel: data.settingYaml?.words?.search?.hideKeyword || '',
            onSelect: handleSelect,
          }}
          searchProps={{
            value: q,
            placeholder,
            options,
            onInput: handleInput,
            onChange: handleChange,
          }}
        />
      </div>
      {breadcrumbWidgetProps && <BreadcrumbWidget {...breadcrumbWidgetProps} />}
    </form>
  )
}
